import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import TimelineIcon from '@mui/icons-material/Timeline';
import RefreshIcon from '@mui/icons-material/Refresh';
import EpisodeCalibratorChart from './EpisodeCalibratorChart';
import DurationDisplayFormatControl from './DurationDisplayFormatControl';
import { detectEpisodesDetailed } from '../../utils/episodeDetection';
import {
  buildEpisodeColumnOptions,
  buildEpisodeColumnOptionsFromOutputFields,
  resolveEpisodeSeriesColumns,
  resolveEpisodeSeriesColumnsFromOutputFields,
  resolveEpisodeGroupCols,
  resolveEpisodeGroupColsFromOutputFields,
  applyEpisodePreserveGroupColumns,
  buildEpisodeGroupColumnOptions,
} from '../../utils/episodeOutputMeta';

export { episodeOutputFields } from '../../utils/episodeOutputMeta';

export const DEFAULT_EPISODE_CONFIG = {
  enabled: true,
  timeColumn: '',
  valueColumn: '',
  groupColumn: '',
  condition: 'below',
  threshold: '',
  exitThreshold: '',
  thresholdHigh: '',
  smoothing: 'none',
  smoothingWindow: 3,
  minDurationMin: 5,
  gapToleranceMin: 0,
  missingAsZero: false,
  output: 'episodes',
  preserveGroupColumns: undefined,
  columnLabels: {},
  durationDisplayFormat: undefined,
  durationTooltipFormat: undefined,
  columnDurationFormats: {},
  resultOps: {
    filters: [],
    sortField: undefined,
    sortDir: 'desc',
    limit: undefined,
  },
  xDateDisplayFormat: undefined,
  xDateBucket: undefined,
};

const CONDITION_OPTIONS = [
  { value: 'below', label: 'Menor que el umbral (ej: sin stock)' },
  { value: 'above', label: 'Mayor que el umbral (ej: sobre 3 unidades)' },
  { value: 'insideBand', label: 'Dentro de una banda (entre A y B)' },
  { value: 'outsideBand', label: 'Fuera de una banda (bajo A o sobre B)' },
];

const isBand = (condition) => condition === 'insideBand' || condition === 'outsideBand';

function fmtDuration(min) {
  if (min >= 90) return `${Math.floor(min / 60)}h ${Math.round(min % 60)}m`;
  return `${Math.round(min)} min`;
}

function groupLabel(groupValues, groupColumns) {
  if (!groupColumns?.length) return 'Serie completa';
  return groupColumns.map((base) => String(groupValues?.[base] ?? '—')).join(' · ');
}

/**
 * Modal calibrador del análisis de episodios: muestra la serie de referencia,
 * permite arrastrar umbrales sobre el gráfico y simula la detección en vivo
 * con los mismos algoritmos que aplica el servidor al publicar.
 */
export default function EpisodeAnalysisDialog({
  open,
  onClose,
  value,
  onChange,
  seriesReady,
  groupColumns = [],
  columns = [],
  context = null,
  episodeInputSource = null,
  episodeInputFields = null,
  fetchSeries, // async () => { groups, groupColumns, hint?, rowCount? }
}) {
  const [cfg, setCfg] = useState(value || DEFAULT_EPISODE_CONFIG);
  const [seriesGroups, setSeriesGroups] = useState([]);
  const [calibrationGroupValue, setCalibrationGroupValue] = useState('');
  const [seriesLoading, setSeriesLoading] = useState(false);
  const [seriesError, setSeriesError] = useState('');

  useEffect(() => {
    if (!open) return;
    const base = value || DEFAULT_EPISODE_CONFIG;
    const fromZones = episodeInputSource?.source === 'zones';
    const resolved = fromZones
      ? resolveEpisodeSeriesColumnsFromOutputFields(episodeInputFields, base)
      : resolveEpisodeSeriesColumns(columns, base, context);
    setCfg({
      ...base,
      timeColumn: resolved.timeColumn || base.timeColumn || '',
      valueColumn: resolved.valueColumn || base.valueColumn || '',
    });
  }, [open, value, columns, context, episodeInputSource, episodeInputFields]);

  const columnOptions = useMemo(() => {
    if (episodeInputSource?.source === 'zones' && episodeInputFields?.length) {
      return buildEpisodeColumnOptionsFromOutputFields(episodeInputFields);
    }
    return buildEpisodeColumnOptions(columns, context);
  }, [columns, context, episodeInputSource, episodeInputFields]);

  const eligibleGroupColumns = useMemo(() => {
    if (episodeInputSource?.source === 'zones' && episodeInputFields?.length) {
      return resolveEpisodeGroupColsFromOutputFields(episodeInputFields, cfg);
    }
    return resolveEpisodeGroupCols(columns, cfg);
  }, [columns, cfg, episodeInputSource, episodeInputFields]);

  const resolvedColumns = useMemo(
    () =>
      episodeInputSource?.source === 'zones'
        ? resolveEpisodeSeriesColumnsFromOutputFields(episodeInputFields, cfg)
        : resolveEpisodeSeriesColumns(columns, cfg, context),
    [columns, cfg, context, episodeInputSource, episodeInputFields]
  );

  const loadSeries = useCallback(async () => {
    if (!fetchSeries) return;
    setSeriesLoading(true);
    setSeriesError('');
    try {
      const result = await fetchSeries(cfg);
      const groups = Array.isArray(result?.groups)
        ? result.groups
        : Array.isArray(result)
          ? [{ key: '', label: 'Serie completa', groupValues: {}, points: result }]
          : [];
      setSeriesGroups(groups);
      if (!groups.length) {
        const hint = result?.hint || '';
        const rowCount = result?.rowCount;
        setSeriesError(
          hint ||
            (rowCount === 0
              ? 'La vista previa no devolvió filas. Revisa filtros, rango de fechas y límite de filas.'
              : 'No se pudieron leer fecha y medida en las filas. Verifica las columnas elegidas abajo.')
        );
      }
    } catch (e) {
      setSeriesGroups([]);
      setSeriesError(e?.message || 'No se pudo cargar la serie de referencia.');
    } finally {
      setSeriesLoading(false);
    }
  }, [fetchSeries, cfg]);

  useEffect(() => {
    if (open && seriesReady && cfg.timeColumn && cfg.valueColumn) loadSeries();
    if (!open) {
      setSeriesGroups([]);
      setCalibrationGroupValue('');
      setSeriesError('');
    }
  }, [open, seriesReady, loadSeries, cfg.timeColumn, cfg.valueColumn, cfg.groupColumn]);

  const groupColumnOptions = useMemo(() => {
    const opts = buildEpisodeGroupColumnOptions(eligibleGroupColumns);
    const current = String(cfg.groupColumn || '').trim();
    if (current && !opts.some((o) => o.value === current)) {
      opts.unshift({ value: current, label: current.replace(/_/g, ' ') });
    }
    return opts;
  }, [eligibleGroupColumns, cfg.groupColumn]);

  const set = useCallback((patch) => setCfg((prev) => ({ ...prev, ...patch })), []);

  const isGroupPreserved = useCallback(
    (base) => {
      const keep = cfg.preserveGroupColumns;
      if (!Array.isArray(keep) || keep.length === 0) return true;
      return keep.some((k) => String(k).toLowerCase() === String(base).toLowerCase());
    },
    [cfg.preserveGroupColumns]
  );

  const togglePreserveGroup = useCallback(
    (base, checked) => {
      const eligible = eligibleGroupColumns.map((g) => g.base);
      let next;
      if (checked) {
        const current = Array.isArray(cfg.preserveGroupColumns)
          ? [...cfg.preserveGroupColumns]
          : [...eligible];
        if (!current.some((k) => String(k).toLowerCase() === String(base).toLowerCase())) {
          current.push(base);
        }
        next = current.filter((b) =>
          eligible.some((g) => String(g).toLowerCase() === String(b).toLowerCase())
        );
        if (next.length >= eligible.length) next = undefined;
      } else {
        next = (Array.isArray(cfg.preserveGroupColumns) ? cfg.preserveGroupColumns : eligible).filter(
          (b) => String(b).toLowerCase() !== String(base).toLowerCase()
        );
      }
      set({ preserveGroupColumns: next?.length ? next : undefined });
    },
    [cfg.preserveGroupColumns, eligibleGroupColumns, set]
  );

  const calibrationValueOptions = useMemo(() => {
    const groupCol = String(cfg.groupColumn || '').trim();
    if (!groupCol || !seriesGroups.length) return [];
    const seen = new Set();
    const opts = [];
    for (const g of seriesGroups) {
      const val = String(g.groupValues?.[groupCol] ?? '—');
      if (seen.has(val)) continue;
      seen.add(val);
      opts.push({ value: val, label: val });
    }
    return opts.sort((a, b) => a.label.localeCompare(b.label));
  }, [cfg.groupColumn, seriesGroups]);

  useEffect(() => {
    const groupCol = String(cfg.groupColumn || '').trim();
    if (!open || !groupCol) {
      setCalibrationGroupValue('');
      return;
    }
    if (
      !calibrationGroupValue ||
      !calibrationValueOptions.some((o) => o.value === calibrationGroupValue)
    ) {
      setCalibrationGroupValue(calibrationValueOptions[0]?.value || '');
    }
  }, [open, cfg.groupColumn, calibrationGroupValue, calibrationValueOptions]);

  const selectedGroup = useMemo(() => {
    if (!seriesGroups.length) return null;
    const groupCol = String(cfg.groupColumn || '').trim();
    if (!groupCol) {
      if (seriesGroups.length === 1) return seriesGroups[0];
      const merged = {
        key: '',
        label: 'Serie completa',
        groupValues: {},
        points: [],
      };
      for (const g of seriesGroups) {
        merged.points.push(...(g.points || []));
      }
      merged.points.sort((a, b) => a.ms - b.ms);
      return merged;
    }
    const want = calibrationGroupValue || calibrationValueOptions[0]?.value || '';
    const matching = seriesGroups.filter(
      (g) => String(g.groupValues?.[groupCol] ?? '—') === want
    );
    if (!matching.length) return seriesGroups[0];
    if (matching.length === 1) return matching[0];
    const merged = {
      key: matching[0].key,
      label: want,
      groupValues: { [groupCol]: want },
      points: [],
    };
    for (const g of matching) {
      merged.points.push(...(g.points || []));
    }
    merged.points.sort((a, b) => a.ms - b.ms);
    return merged;
  }, [seriesGroups, cfg.groupColumn, calibrationGroupValue, calibrationValueOptions]);
  const points = selectedGroup?.points || [];

  const thresholdMissing =
    cfg.enabled && (cfg.threshold === '' || cfg.threshold === null || Number.isNaN(Number(cfg.threshold)));
  const bandMissing =
    cfg.enabled &&
    isBand(cfg.condition) &&
    (cfg.thresholdHigh === '' || cfg.thresholdHigh === null || Number.isNaN(Number(cfg.thresholdHigh)));

  useEffect(() => {
    if (!points.length || !thresholdMissing) return;
    let max = -Infinity;
    for (const p of points) if (p.value > max) max = p.value;
    if (Number.isFinite(max)) {
      const half = max > 4 ? Math.round(max / 2) : Math.round((max / 2) * 10) / 10;
      set({ threshold: half });
    }
  }, [points, thresholdMissing, set]);

  const detection = useMemo(() => {
    if (!points.length || thresholdMissing || bandMissing) {
      return { episodes: [], discarded: [], smoothed: [], filledPoints: [], bucketMs: 60000 };
    }
    return detectEpisodesDetailed(points, cfg);
  }, [points, cfg, thresholdMissing, bandMissing]);

  const stats = useMemo(() => {
    const eps = detection.episodes || [];
    const totalMin = eps.reduce((acc, e) => acc + (e.endMs - e.startMs) / 60000, 0);
    const maxMin = eps.reduce((acc, e) => Math.max(acc, (e.endMs - e.startMs) / 60000), 0);
    return {
      count: eps.length,
      totalMin,
      maxMin,
      discardedCount: (detection.discarded || []).length,
    };
  }, [detection]);

  const handleSave = () => {
    const resolved = resolveEpisodeSeriesColumns(columns, cfg, context);
    const next = {
      ...cfg,
      timeColumn: cfg.timeColumn || resolved.timeColumn,
      valueColumn: cfg.valueColumn || resolved.valueColumn,
      groupColumn: cfg.groupColumn || undefined,
    };
    onChange(next.enabled ? next : null);
    onClose();
  };

  const handleDisable = () => {
    onChange(null);
    onClose();
  };

  const numField = (key, label, widthPx, extra = {}) => (
    <TextField
      size="small"
      type="number"
      label={label}
      value={cfg[key]}
      onChange={(e) => set({ [key]: e.target.value })}
      disabled={!cfg.enabled}
      sx={{ width: widthPx }}
      {...extra}
    />
  );

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <TimelineIcon color="primary" fontSize="small" />
        Análisis de episodios (tiempo en estado)
        <Box sx={{ flex: 1 }} />
        <FormControlLabel
          control={<Switch checked={cfg.enabled} onChange={(e) => set({ enabled: e.target.checked })} />}
          label="Activar"
          sx={{ mr: 0 }}
        />
      </DialogTitle>
      <DialogContent dividers sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        {episodeInputSource && (
          <Alert severity="info" sx={{ py: 0.75 }}>
            Fuente de datos: <b>{episodeInputSource.label}</b>
            {episodeInputSource.source === 'zones'
              ? ' — elige columnas de la tabla de zonas (p. ej. periodo, eventos).'
              : ' — columnas del SQL base (time, probability unnest, etc.).'}
          </Alert>
        )}

        {!seriesReady && (
          <Alert severity="warning">
            {episodeInputSource?.source === 'zones'
              ? 'Activa y configura zonas primero, o elige columnas de tiempo/medida de su salida.'
              : 'El dataset necesita al menos una columna de eje/fecha y una medida (suma o conteo).'}
          </Alert>
        )}

        {seriesReady && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 220 }} disabled={!cfg.enabled}>
              <InputLabel>Columna de tiempo</InputLabel>
              <Select
                label="Columna de tiempo"
                value={cfg.timeColumn || resolvedColumns.timeColumn || ''}
                onChange={(e) => set({ timeColumn: e.target.value })}
              >
                {columnOptions.timeOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 220 }} disabled={!cfg.enabled}>
              <InputLabel>Columna medida</InputLabel>
              <Select
                label="Columna medida"
                value={cfg.valueColumn || resolvedColumns.valueColumn || ''}
                onChange={(e) => set({ valueColumn: e.target.value })}
              >
                {columnOptions.valueOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 220 }} disabled={!cfg.enabled}>
              <InputLabel>Columna de grupo</InputLabel>
              <Select
                label="Columna de grupo"
                value={cfg.groupColumn || ''}
                onChange={(e) => set({ groupColumn: e.target.value })}
              >
                <MenuItem value="">Ninguna (serie completa)</MenuItem>
                {groupColumnOptions.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              size="small"
              startIcon={<RefreshIcon fontSize="small" />}
              onClick={loadSeries}
              disabled={!cfg.timeColumn || !cfg.valueColumn || seriesLoading}
            >
              Recargar serie
            </Button>
          </Box>
        )}

        {seriesReady && eligibleGroupColumns.length > 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Columnas del paso anterior en la salida
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Elige qué columnas del paso anterior aparecen en la tabla de episodios. La detección se
              parte solo por la columna de grupo elegida arriba (cualquier columna salvo tiempo y medida).
            </Typography>
            <FormGroup row sx={{ gap: 0.5 }}>
              {eligibleGroupColumns.map((g) => (
                <FormControlLabel
                  key={g.base}
                  control={
                    <Checkbox
                      size="small"
                      checked={isGroupPreserved(g.base)}
                      onChange={(e) => togglePreserveGroup(g.base, e.target.checked)}
                      disabled={!cfg.enabled}
                    />
                  }
                  label={g.base.replace(/_/g, ' ')}
                />
              ))}
            </FormGroup>
          </Box>
        )}

        {seriesReady && cfg.groupColumn && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ width: '100%' }}>
              Calibración
            </Typography>
            {calibrationValueOptions.length > 0 && (
              <FormControl size="small" sx={{ minWidth: 220 }} disabled={!cfg.enabled}>
                <InputLabel>Valor a calibrar</InputLabel>
                <Select
                  label="Valor a calibrar"
                  value={calibrationGroupValue}
                  onChange={(e) => setCalibrationGroupValue(e.target.value)}
                >
                  {calibrationValueOptions.map((o) => (
                    <MenuItem key={o.value} value={o.value}>
                      {o.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <Typography variant="caption" color="text.secondary">
              Vista previa con{' '}
              <b>{selectedGroup?.label || calibrationGroupValue || '…'}</b>. La detección final corre
              por separado en cada {String(cfg.groupColumn).replace(/_/g, ' ')} del dataset.
            </Typography>
          </Box>
        )}

        {seriesError && (
          <Alert severity="warning" sx={{ py: 0.5 }}>
            {seriesError}
          </Alert>
        )}

        {seriesReady && (
          <Box sx={{ position: 'relative' }}>
            {seriesLoading && (
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  zIndex: 2,
                  bgcolor: 'rgba(255,255,255,0.6)',
                }}
              >
                <CircularProgress size={28} />
              </Box>
            )}
            <EpisodeCalibratorChart
              points={points}
              smoothed={detection.smoothed}
              filledPoints={detection.filledPoints}
              episodes={detection.episodes}
              discarded={detection.discarded}
              cfg={cfg}
              onCfgChange={set}
              height={300}
            />
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mt: 0.5 }}>
              <Chip
                size="small"
                label={`${stats.count} episodios`}
                color={stats.count ? 'error' : 'default'}
                variant={stats.count ? 'filled' : 'outlined'}
              />
              <Chip size="small" variant="outlined" label={`total ${fmtDuration(stats.totalMin)}`} />
              <Chip size="small" variant="outlined" label={`más largo ${fmtDuration(stats.maxMin)}`} />
              {stats.discardedCount > 0 && (
                <Tooltip title="Episodios que cumplen la condición pero duran menos que la duración mínima (ruido filtrado). Se ven en gris en el gráfico.">
                  <Chip
                    size="small"
                    variant="outlined"
                    color="default"
                    label={`${stats.discardedCount} descartados por ruido`}
                  />
                </Tooltip>
              )}
              <Box sx={{ flex: 1 }} />
              <Typography variant="caption" color="text.secondary">
                Arrastra las líneas de umbral · zona roja = episodio · gris = descartado · selecciona ventana abajo
              </Typography>
              <Button size="small" startIcon={<RefreshIcon fontSize="small" />} onClick={loadSeries} disabled={seriesLoading}>
                Recargar serie
              </Button>
            </Box>
            {seriesError && (
              <Alert severity="warning" sx={{ mt: 0.5, py: 0 }}>
                {seriesError}
              </Alert>
            )}
          </Box>
        )}

        <Divider />

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 250 }} disabled={!cfg.enabled}>
            <InputLabel>Condición</InputLabel>
            <Select
              label="Condición"
              value={cfg.condition}
              onChange={(e) => set({ condition: e.target.value, exitThreshold: '' })}
            >
              {CONDITION_OPTIONS.map((o) => (
                <MenuItem key={o.value} value={o.value}>
                  {o.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {numField('threshold', isBand(cfg.condition) ? 'Umbral A (inferior)' : 'Umbral', 140, {
            error: thresholdMissing,
          })}
          {isBand(cfg.condition) &&
            numField('thresholdHigh', 'Umbral B (superior)', 145, { error: bandMissing })}
          {!isBand(cfg.condition) && (
            <Tooltip title="Histéresis: salir del estado exige cruzar este segundo umbral. Evita que valores que oscilan alrededor del umbral corten el episodio.">
              {numField('exitThreshold', 'Umbral de salida', 140, { placeholder: 'opcional' })}
            </Tooltip>
          )}
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
          <Tooltip title="Descartar episodios más cortos (ruido puntual). Se muestran en gris en el gráfico.">
            {numField('minDurationMin', 'Duración mínima (min)', 165, { inputProps: { min: 0 } })}
          </Tooltip>
          <Tooltip title="Unir episodios separados por una interrupción corta.">
            {numField('gapToleranceMin', 'Tolerancia a huecos (min)', 180, { inputProps: { min: 0 } })}
          </Tooltip>
          <FormControl size="small" sx={{ minWidth: 185 }} disabled={!cfg.enabled}>
            <InputLabel>Suavizado</InputLabel>
            <Select label="Suavizado" value={cfg.smoothing || 'none'} onChange={(e) => set({ smoothing: e.target.value })}>
              <MenuItem value="none">Sin suavizado</MenuItem>
              <MenuItem value="median">Mediana móvil (recomendado)</MenuItem>
              <MenuItem value="mean">Promedio móvil</MenuItem>
            </Select>
          </FormControl>
          {cfg.smoothing && cfg.smoothing !== 'none' && (
            <FormControl size="small" sx={{ minWidth: 120 }} disabled={!cfg.enabled}>
              <InputLabel>Ventana</InputLabel>
              <Select
                label="Ventana"
                value={cfg.smoothingWindow || 3}
                onChange={(e) => set({ smoothingWindow: e.target.value })}
              >
                {[3, 5, 7, 9, 11].map((w) => (
                  <MenuItem key={w} value={w}>
                    {w} buckets
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={Boolean(cfg.missingAsZero)}
                onChange={(e) => set({ missingAsZero: e.target.checked })}
                disabled={!cfg.enabled}
              />
            }
            label={
              <Tooltip title="Útil si la serie viene de conteo de eventos: sin filas = sin detecciones.">
                <Typography variant="body2">Minutos sin datos = 0</Typography>
              </Tooltip>
            }
          />
        </Box>

        {cfg.enabled && (
          <>
            <Typography variant="caption" color="text.secondary" fontWeight={600}>
              Formato de columnas de duración (minutos → horas, días…)
            </Typography>
            <DurationDisplayFormatControl
              displayFormat={cfg.durationDisplayFormat}
              tooltipFormat={cfg.durationTooltipFormat}
              onDisplayChange={(v) => set({ durationDisplayFormat: v })}
              onTooltipChange={(v) => set({ durationTooltipFormat: v })}
              previewMinutes={Math.round(stats.maxMin || stats.totalMin || 107)}
            />
            <Typography variant="caption" color="text.secondary">
              El formato de la tabla, nombres de columnas, orden, filtros y Top N se configuran en la
              pestaña «Episodios». Quién alimenta el gráfico lo define el pipeline (paso más a la derecha).
            </Typography>
          </>
        )}
      </DialogContent>
      <DialogActions>
        {value?.enabled && (
          <Button color="error" onClick={handleDisable} sx={{ mr: 'auto' }}>
            Quitar análisis
          </Button>
        )}
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={handleSave} disabled={cfg.enabled && (thresholdMissing || bandMissing)}>
          Aplicar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
